import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

// Dueño y trabajador tienen cookies distintas y pueden convivir en el mismo
// navegador (una pestaña en el panel, otra en /t). Quién eres no lo adivina el
// backend: se lo dice esta cabecera. Estar en /t = eres el trabajador.
// Ojo: la ruta es exactamente "/t". Un startsWith("/t") pelado se tragaría
// /terminos y mandaría la web pública en modo trabajador.
export const MODO_TRABAJADOR_HEADER = "X-TAH-Modo";
export const enModoTrabajador = () => {
  if (typeof window === "undefined") return false;
  const p = window.location.pathname;
  return p === "/t" || p.startsWith("/t/");
};

api.interceptors.request.use((config) => {
  if (enModoTrabajador()) {
    config.headers = config.headers || {};
    config.headers[MODO_TRABAJADOR_HEADER] = "trabajador";
  }
  return config;
});

// Rutas públicas donde un 401 es esperado (no hace falta redirigir)
const PUBLIC_PATHS = ["/", "/login", "/registro", "/forgot-password", "/reset-password", "/verify-email", "/calculadora", "/auth-callback"];

api.interceptors.response.use(
  (r) => r,
  async (error) => {
    if (error?.response?.status === 401) {
      const config = error.config || {};
      // La cookie de sesión (recién puesta por /auth/login) puede tardar un instante en
      // estar disponible para la siguiente petición inmediata. Antes de dar la sesión por
      // caducada, reintentamos una vez tras una pequeña espera.
      if (!config._retriedAfter401) {
        config._retriedAfter401 = true;
        await new Promise((resolve) => setTimeout(resolve, 400));
        try {
          return await api(config);
        } catch (retryError) {
          error = retryError;
        }
      }
    }
    // Si la sesión ha expirado o no existe (tras el reintento), redirigir a /login
    // (excepto en rutas públicas). Devolvemos un promise que NUNCA rechaza para evitar
    // overlay de runtime errors en pantalla por 401s legítimos del flujo de autenticación.
    if (error?.response?.status === 401) {
      const path = window.location.pathname;
      const isPublic = PUBLIC_PATHS.some((p) => path === p || path.startsWith(p + "/"));
      if (!isPublic && path.startsWith("/app")) {
        // Forzar logout client-side y redirigir
        window.location.replace("/login");
      }
      // Devolver respuesta "vacía" para que .then() no rompa pero los callers que esperaban datos vean null/undefined
      return Promise.reject(error);
    }
    return Promise.reject(error);
  },
);

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Algo ha salido mal. Inténtalo de nuevo.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

// task-camarero10-b4-distinguir-409-candado: algunos errores de backend (p.ej.
// el 409 del candado de sync de Camarero10) llegan con `detail` como objeto
// estructurado `{ msg, code }`, a diferencia del `detail` string plano de la
// mayoría de errores. Este helper aísla la lectura de ese `code` para que los
// callers no dupliquen la comprobación "es objeto y no array" en cada sitio.
export function getApiErrorCode(error) {
  const detail = error?.response?.data?.detail;
  if (detail && typeof detail === "object" && !Array.isArray(detail)) {
    return detail.code;
  }
  return undefined;
}
